<template>
  <div class="relative flex h-[52px] items-center">
    <template v-if="isCurrentMonth">
      <div v-if="showRangeBg" class="absolute left-0 right-0 top-1/2 h-9 -translate-y-1/2 bg-[#F4F1ED]" :class="rangeBgPositionClass" />
      <button
        class="relative z-10 flex w-full items-center justify-center py-2 font-Spectral text-[16px] md:text-[17px]"
        type="button"
        :data-testid="dayTestId"
        :disabled="isDisabled"
        @click="onClick"
      >
        <span :class="dayIndicatorClass">
          {{ dayOfMonth }}
        </span>
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { Dayjs } from 'dayjs';

interface Props {
  day: Dayjs;
  isCurrentMonth: boolean;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  selectionPhase: 'start' | 'end';
  minDate: Dayjs;
  maxDate: Dayjs;
  onClick?: (day: Dayjs) => void;
}

const props = defineProps<Props>();

const dayOfMonth = computed(() => props.day.date());

const isStartDate = computed(() => props.startDate?.isSame(props.day, 'day') ?? false);
const isEndDate = computed(() => props.endDate?.isSame(props.day, 'day') ?? false);
const isInRange = computed(() => {
  if (!props.startDate || !props.endDate) return false;
  return props.day.isAfter(props.startDate, 'day') && props.day.isBefore(props.endDate, 'day');
});

const isSameStartEnd = computed(() => {
  if (!props.startDate || !props.endDate) return false;
  return props.startDate.isSame(props.endDate, 'day');
});

const isDisabled = computed((): boolean => {
  if (props.day.isBefore(props.minDate)) return true;
  if (props.day.isAfter(props.maxDate)) return true;
  if (props.selectionPhase === 'end' && props.startDate && !props.day.isAfter(props.startDate, 'day')) return true;
  return false;
});

const showRangeBg = computed(() => {
  if (!props.startDate || !props.endDate || isSameStartEnd.value) return false;
  return isStartDate.value || isEndDate.value || isInRange.value;
});

const rangeBgPositionClass = computed(() => {
  if (isStartDate.value) return 'left-1/2 right-0';
  if (isEndDate.value) return 'left-0 right-1/2';
  return 'left-0 right-0';
});

const dayIndicatorClass = computed(() => {
  if (isStartDate.value || isEndDate.value) {
    return 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-gold3 text-white';
  }
  if (isInRange.value) return 'text-gray9';
  if (isDisabled.value) {
    return 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm text-gray-400 opacity-60';
  }
  return 'text-gray9 cursor-pointer hover:bg-black/5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm';
});

const dayTestId = computed(() => `duration-day-${props.day.format('YYYY-MM-DD')}`);

const onClick = () => {
  if (isDisabled.value) return;
  props.onClick?.(props.day);
};
</script>
